import { BaseFeature } from '../../core/base-feature.js';

/**
 * BlackjackFeature - Frontend feature for Blackjack game
 *
 * Controls the desktop overlay game from the frontend.
 * Receives full game state including dealer hole card, entire shoe,
 * running count, and all cheat/sabotage capabilities.
 */
export class BlackjackFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'blackjack',
            name: 'Blackjack',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'stack_deck', 'swap_hole_card', 'sabotage', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Blackjack] Initializing...');
        console.log('[Blackjack] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'get_state_response':
            case 'end_response':
            case 'stack_deck_response':
            case 'swap_hole_card_response':
            case 'sabotage_response':
            case 'set_auto_cheat_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Blackjack] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    // ─── Game Control ───

    startGame() {
        return this._sendRequest('start');
    }

    getState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // ─── Cheat Actions ───

    stackDeck(cardSequence) {
        return this._sendRequest('stack_deck', { cardSequence });
    }

    swapHoleCard(newCard) {
        return this._sendRequest('swap_hole_card', { newCard });
    }

    sabotage(sabotageAction) {
        return this._sendRequest('sabotage', { sabotageAction });
    }

    setAutoCheat(enabled) {
        return this._sendRequest('set_auto_cheat', { enabled });
    }

    // ─── State Subscriptions ───

    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Blackjack] Error in state callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[Blackjack] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
